import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import { CardBlock } from '@elfsight/widget-ui';
import { useRect } from 'react-use-rect';
import tinycolor from 'tinycolor2';
import type { Job, JobBoardSettings, SalaryValue } from '../../../../types';
import { useFormatSalaryValue } from '../../../utils';
import { isOffsetLogo, LogoContainer } from './Logo';

interface TitleBlockProps extends Partial<Job> {
  salary: SalaryValue;
  isShowSalary: boolean;
  isShowCompany: boolean;
  isShowDepartment: boolean;
  isShowLocation: boolean;
  isShowTypeOfContract: boolean;
  isShowApplyButton: boolean;
  isShowLearnMoreButton: boolean;
  isShowJobLogo: boolean;
  withCover: boolean;
  color: string;
  jobTitleStyle: TextStyleValue;
  jobSalaryStyle: TextStyleValue;
  jobCaptionStyle: TextStyleValue;
  setTitleHeight?: Function;
  titleHeight?: number;
  cardWidth?: number;
  isPopup?: boolean;
  titleFontStyle?: TextStyleValue;
  captionFontStyle?: TextStyleValue;
  salaryFontStyle?: TextStyleValue;
  salaryAlign?: 'flex-start' | 'center' | 'flex-end';
}

export const TitleBlock = ({
  title,
  salary,
  company,
  department,
  location,
  link,
  typeOfContract,
  isShowSalary,
  isShowCompany,
  isShowDepartment,
  isShowLocation,
  isPopup,
  isShowTypeOfContract,
  color,
  jobTitleStyle,
  jobSalaryStyle,
  jobCaptionStyle,
  titleFontStyle,
  captionFontStyle,
  salaryFontStyle,
  setTitleHeight,
  titleHeight,
  isShowApplyButton,
  isShowLearnMoreButton,
  salaryAlign,

  isShowJobLogo,
  logo,
  withCover,
  cardWidth
}: TitleBlockProps) => {
  const itemsToDisplay = {
    company: !!company && isShowCompany,
    department: !!department && isShowDepartment,
    location: !!location && isShowLocation,
    typeOfContract: !!typeOfContract && isShowTypeOfContract,
    salary: salary.type !== 'none' && isShowSalary
  };

  const isShowTitleInfo = Object.values(itemsToDisplay).some(Boolean);

  const formatSalary = useFormatSalaryValue();
  const [height, setHeight] = useState(0);
  const [ref] = useRect(({ height }) => setHeight(height), {
    resize: true
  });

  useEffect(() => {
    if (titleHeight !== height) {
      setTitleHeight?.(height);
    }
  }, [setTitleHeight, height]);

  const isShowTitleBlock = title || isShowTitleInfo;

  if (!isShowTitleBlock) {
    return null;
  }

  return (
    <Container
      ref={ref}
      _isCentered={!isShowApplyButton || !link || !isShowLearnMoreButton}
      _withCover={withCover}
      _cardWidth={cardWidth}
      _isPopup={isPopup}
    >
      <Show when={isShowJobLogo}>
        <LogoContainer
          src={logo?.url}
          alt={company || title}
          withCover={withCover}
          cardWidth={cardWidth}
          isPopup={isPopup}
        />
      </Show>

      <TitleCaptionAndSalary>
        <TitleAndCaption>
          <Show when={!!title}>
            <Title
              _fontSize={titleFontStyle?.fontSize ?? jobTitleStyle.fontSize}
              _fontWeight={
                titleFontStyle?.fontWeight ?? jobTitleStyle.fontWeight
              }
              _color={color}
            >
              {title}
            </Title>
          </Show>

          <Show when={isShowTitleInfo}>
            <TitleInfo
              _isPopup={isPopup}
              _fontSize={captionFontStyle?.fontSize ?? jobCaptionStyle.fontSize}
              _color={color}
            >
              <Show when={itemsToDisplay.company}>
                <InfoItem _isPopup={isPopup}>{company}</InfoItem>
              </Show>
              <Show when={itemsToDisplay.department}>
                <InfoItem _isPopup={isPopup}>{department}</InfoItem>
              </Show>
              <Show when={itemsToDisplay.location}>
                <InfoItem _isPopup={isPopup}>{location}</InfoItem>
              </Show>
              <Show when={itemsToDisplay.typeOfContract}>
                <InfoItem _isPopup={isPopup}>{typeOfContract}</InfoItem>
              </Show>
            </TitleInfo>
          </Show>
        </TitleAndCaption>

        <Show when={itemsToDisplay.salary}>
          <Salary
            _color={color}
            _fontSize={salaryFontStyle?.fontSize ?? jobSalaryStyle.fontSize}
            _fontWeight={
              salaryFontStyle?.fontWeight ?? jobSalaryStyle.fontWeight
            }
            _salaryAlign={
              !isPopup &&
              (!link || !isShowApplyButton || !isShowLearnMoreButton) &&
              (salaryAlign ?? 'center')
            }
          >
            {formatSalary(salary)}
          </Salary>
        </Show>
      </TitleCaptionAndSalary>
    </Container>
  );
};

const Container = styled(CardBlock)<{
  _withCover: boolean;
  _isCentered?: boolean;
  _cardWidth?: number;
  _isPopup?: boolean;
}>`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  justify-self: flex-start;

  &&& {
    height: auto;
    flex-grow: 0;

    &:first-child:last-child {
      ${({ _isCentered }) =>
        _isCentered &&
        css`
          margin: auto 0;
        `}
    }
  }

  ${(props) =>
    isOffsetLogo(props) &&
    css`
      flex-direction: column;
      align-items: start;
    `}
`;

const TitleCaptionAndSalary = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  justify-self: flex-start;
`;

const TitleAndCaption = styled.div`
  display: flex;
  flex-direction: column;
`;

const Salary = styled.div<{
  _color?: string;
  _fontSize?: number;
  _fontWeight?: 'normal' | 'bold' | number;
  _salaryAlign?: 'flex-start' | 'center' | 'flex-end' | false;
}>`
  font-size: ${({ _fontSize }) => _fontSize ?? '16'}px;
  line-height: ${({ _fontSize }) => (_fontSize ? _fontSize * 1.375 : '22')}px;
  color: ${({ _color }) => _color ?? '#111'};
  margin-left: ${({ _fontSize }) => (_fontSize ? _fontSize * 0.5 : '8')}px;
  align-self: ${({ _salaryAlign }) => _salaryAlign ?? 'flex-start'};
  font-weight: ${({ _fontWeight }) => _fontWeight};
  max-width: 150px;
  flex-shrink: 1;
  text-align: end;
`;

const Title = styled.div<{
  _color?: string;
  _fontSize?: number;
  _fontWeight?: 'normal' | 'bold' | number;
}>`
  font-size: ${({ _fontSize }) => _fontSize ?? '16'}px;
  font-weight: ${({ _fontWeight }) => _fontWeight};
  line-height: ${({ _fontSize }) => (_fontSize ? _fontSize * 1.375 : '22')}px;
  color: ${({ _color }) => _color ?? '#111'};
`;

export const InfoItem = styled.div<{ _isPopup?: boolean }>`
  display: flex;

  &:not(:first-child):before {
    content: '';
    display: inline-block;
    width: 2px;
    height: 2px;
    border-radius: 50%;
    flex-shrink: 0;
    margin: 0 6px;
    background-color: rgba(17, 17, 17, 0.5);
  }
`;

export const TitleInfo = styled.div<{
  _isPopup?: boolean;
  _fontSize?: number;
  _fontWeight?: 'normal' | 'bold' | number;
  _color?: string;
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${({ _isPopup }) => (_isPopup ? 8 : 4)}px;

  & ${InfoItem} {
    color: ${({ _color }) =>
      tinycolor(_color || '#111')
        .setAlpha(0.5)
        .toString()};
    font-size: ${({ _fontSize }) => _fontSize}px;
    line-height: ${({ _fontSize }) => _fontSize && _fontSize * 1.28571}px;

    &:not(:first-child):before {
      width: 2px;
      height: 2px;
      margin: ${({ _fontSize }) => (Number(_fontSize) * 1.28571) / 2}px 6px 0;
      background-color: ${({ _color }) =>
        tinycolor(_color || '#111')
          .setAlpha(0.5)
          .toString()};
    }
  }
`;

export const getTitleBlockSettings = (settings: JobBoardSettings) => {
  return {
    isShowSalary: settings.isShowSalary,
    isShowCompany: settings.isShowCompany,
    isShowDepartment: settings.isShowDepartment,
    isShowLocation: settings.isShowLocation,
    isShowTypeOfContract: settings.isShowTypeOfContract,
    jobTitleStyle: settings.jobTitleStyle,
    jobSalaryStyle: settings.jobSalaryStyle,
    jobCaptionStyle: settings.jobCaptionStyle,
    isShowApplyButton: settings.isShowApplyButton,
    isShowLearnMoreButton: settings.isShowLearnMoreButton,
    isShowJobLogo: settings.isShowJobLogo
  };
};
